// src/services/EntityService.js
import storage from './StorageAdapter';
import auditService from './AuditService';

const STORAGE_KEY = 'rms_entities';
const ACTIVE_ENTITY_KEY = 'rms_active_entity';

const ENTITY_TYPES = ['parent', 'subsidiary', 'branch', 'joint_venture'];

const INITIAL_ENTITIES = [
  {
    id: 'ent-001',
    name: 'Prime Financial Group AB',
    orgNumber: '556000-0001',
    type: 'parent',
    parentId: null,
    description: '',
    country: 'SE',
    created: '2025-01-15T08:00:00',
    updated: '2025-01-15T08:00:00'
  }
];

class EntityService {
  constructor() {
    this.listeners = [];
    this._ensureData();
  }

  _ensureData() {
    const existing = storage.getJSON(STORAGE_KEY);
    if (!existing) {
      storage.setJSON(STORAGE_KEY, INITIAL_ENTITIES);
    }
  }

  subscribe(fn) {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  _notify() {
    this.listeners.forEach(fn => fn());
  }

  getEntities() {
    return storage.getJSON(STORAGE_KEY, []);
  }

  getEntity(id) {
    return this.getEntities().find(e => e.id === id) || null;
  }

  getRootEntities() {
    return this.getEntities().filter(e => !e.parentId);
  }

  getChildren(parentId) {
    return this.getEntities().filter(e => e.parentId === parentId);
  }

  getTree() {
    const entities = this.getEntities();
    const buildNode = (entity) => ({
      ...entity,
      children: entities
        .filter(e => e.parentId === entity.id)
        .map(buildNode)
    });
    return entities
      .filter(e => !e.parentId)
      .map(buildNode);
  }

  getEntityPath(id) {
    const entities = this.getEntities();
    const path = [];
    let current = entities.find(e => e.id === id);
    while (current) {
      path.unshift(current);
      const pid = current.parentId;
      current = pid ? entities.find(e => e.id === pid) : null;
    }
    return path;
  }

  async addEntity(data) {
    const entities = this.getEntities();
    const now = new Date().toISOString();
    const newEntity = {
      id: 'ent-' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
      name: data.name || '',
      orgNumber: data.orgNumber || '',
      type: data.type || 'subsidiary',
      parentId: data.parentId || null,
      description: data.description || '',
      country: data.country || 'SE',
      reportColors: data.reportColors || null,
      created: now,
      updated: now
    };
    entities.push(newEntity);
    await storage.setJSON(STORAGE_KEY, entities);
    auditService.logCreate('Admin', 'entity', newEntity.id, { name: newEntity.name });
    this._notify();
    return newEntity;
  }

  async updateEntity(id, data) {
    const entities = this.getEntities();
    const idx = entities.findIndex(e => e.id === id);
    if (idx === -1) return null;

    // Prevent setting parent to self or own descendant
    if (data.parentId) {
      if (data.parentId === id) return null;
      const descendants = this._getDescendantIds(id, entities);
      if (descendants.includes(data.parentId)) return null;
    }

    entities[idx] = {
      ...entities[idx],
      ...data,
      id,
      updated: new Date().toISOString()
    };
    await storage.setJSON(STORAGE_KEY, entities);
    auditService.logUpdate('Admin', 'entity', id, data, `Entity updated: ${entities[idx].name}`);
    this._notify();
    return entities[idx];
  }

  async deleteEntity(id) {
    const entities = this.getEntities();
    const entity = entities.find(e => e.id === id);
    if (!entity) return false;

    // Re-parent children to deleted entity's parent
    const children = entities.filter(e => e.parentId === id);
    children.forEach(child => {
      child.parentId = entity.parentId;
    });

    const filtered = entities.filter(e => e.id !== id);
    await storage.setJSON(STORAGE_KEY, filtered);
    auditService.logDelete('Admin', 'entity', id);
    this._notify();
    return true;
  }

  getDescendantIds(id) {
    return this._getDescendantIds(id, this.getEntities());
  }

  _getDescendantIds(parentId, entities) {
    const result = [];
    const children = entities.filter(e => e.parentId === parentId);
    children.forEach(child => {
      result.push(child.id);
      result.push(...this._getDescendantIds(child.id, entities));
    });
    return result;
  }

  getActiveEntityId() {
    const id = storage.getItem(ACTIVE_ENTITY_KEY);
    if (id && this.getEntity(id)) return id;
    // Fallback to first root entity
    const roots = this.getRootEntities();
    const fallback = roots.length > 0 ? roots[0].id : null;
    if (fallback) storage.setItem(ACTIVE_ENTITY_KEY, fallback);
    return fallback;
  }

  getActiveEntity() {
    const id = this.getActiveEntityId();
    return id ? this.getEntity(id) : null;
  }

  async setActiveEntityId(id) {
    const entity = this.getEntity(id);
    if (!entity) return false;
    await storage.setItem(ACTIVE_ENTITY_KEY, id);
    window.dispatchEvent(new CustomEvent('rms_active_entity_changed', {
      detail: { entityId: id, entity }
    }));
    this._notify();
    return true;
  }

  getEntityReportColors(entityId) {
    const entity = this.getEntity(entityId);
    if (!entity) return null;
    const rc = entity.reportColors;
    if (!rc) return null;
    // Return only if at least one color is set
    if (rc.primary || rc.secondary || rc.accent) return rc;
    return null;
  }

  getEntityCount() {
    return this.getEntities().length;
  }

  async reset() {
    await storage.setJSON(STORAGE_KEY, INITIAL_ENTITIES);
    await storage.removeItem(ACTIVE_ENTITY_KEY);
    this._notify();
  }
}

const entityService = new EntityService();
export { ENTITY_TYPES };
export default entityService;
